import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, KeyRound, ArrowLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getRef } from "../lib/ref";
import { Logo, PebbleMark } from "../components/Logo";

export default function Login() {
  const [email, setEmail] = useState("");
  const [lastName, setLastName] = useState("");
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");
  const [optIn, setOptIn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [matched, setMatched] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = localStorage.getItem("pf_guest_token");
    if (t) {
      try {
        if (JSON.parse(atob(t.split(".")[1])).exp * 1000 > Date.now()) navigate("/guide");
      } catch { /* stay on login */ }
    }
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.post("/auth/magic", { email, opt_in: optIn, ref: getRef(), last_name: lastName, checkin, checkout });
      setMatched(res.data.matched);
      setSent(true);
    } catch {
      toast.error("Couldn't send the link — check the email and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="bg-white border border-charcoal/10 rounded-3xl p-8 sm:p-10 shadow-sm text-center" data-testid="login-card">
          <div className="flex justify-center mb-5"><PebbleMark size={52} /></div>
          {sent ? (
            <div data-testid="login-success">
              <span className="inline-flex p-4 rounded-full bg-sage/15 mb-4"><Mail size={26} className="text-sagedeep" aria-hidden /></span>
              <h1 className="font-serif text-3xl font-semibold text-charcoal">Check your inbox</h1>
              <p className="text-soft mt-3 leading-relaxed">
                We sent a sign-in link to <strong className="text-charcoal">{email}</strong>. It keeps working for a year —
                bookmark it and come back anytime.
              </p>
              {matched === false && (
                <div className="mt-4 bg-[#FBF1EA] border border-copper/40 rounded-2xl px-4 py-3.5 text-left flex gap-3" data-testid="login-match-warning" role="status">
                  <AlertTriangle size={18} className="text-copper shrink-0 mt-0.5" aria-hidden />
                  <p className="text-sm text-charcoal/85 leading-relaxed">
                    We couldn't match that last name and those dates to a current reservation — your link is still on its way,
                    and our team will confirm your booking details. If you mistyped, just send a fresh link below.
                  </p>
                </div>
              )}
              <button onClick={() => { setSent(false); setEmail(""); setMatched(null); }} className="mt-6 text-sm font-semibold text-copperdeep underline underline-offset-4" data-testid="login-again-button">
                Use a different email
              </button>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-3xl font-semibold text-charcoal" data-testid="login-heading">Access Your Welcome Guide</h1>
              <p className="text-soft mt-2 mb-7">A few details from your reservation and we'll send a reusable sign-in link — no password needed.</p>
              <form onSubmit={submit} className="space-y-4 text-left">
                <label className="block">
                  <span className="block text-sm font-semibold text-soft mb-1.5">Email</span>
                  <input
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full bg-cream border border-charcoal/15 rounded-xl px-4 py-3.5 text-base outline-none focus:border-copper"
                    data-testid="login-email-input"
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-semibold text-soft mb-1.5">Last name on the reservation</span>
                  <input
                    type="text" required value={lastName} onChange={(e) => setLastName(e.target.value)}
                    placeholder="e.g. Rivera"
                    className="w-full bg-cream border border-charcoal/15 rounded-xl px-4 py-3.5 text-base outline-none focus:border-copper"
                    data-testid="login-lastname-input"
                  />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="block text-sm font-semibold text-soft mb-1.5">Check-in</span>
                    <input
                      type="date" required value={checkin} onChange={(e) => setCheckin(e.target.value)}
                      className="w-full bg-cream border border-charcoal/15 rounded-xl px-4 py-3.5 text-base outline-none focus:border-copper"
                      data-testid="login-checkin-input"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm font-semibold text-soft mb-1.5">Check-out</span>
                    <input
                      type="date" required value={checkout} onChange={(e) => setCheckout(e.target.value)}
                      className="w-full bg-cream border border-charcoal/15 rounded-xl px-4 py-3.5 text-base outline-none focus:border-copper"
                      data-testid="login-checkout-input"
                    />
                  </label>
                </div>
                <label className="flex items-start gap-3 cursor-pointer bg-cream rounded-xl px-4 py-3.5 border border-charcoal/10" data-testid="login-optin-label">
                  <input type="checkbox" checked={optIn} onChange={(e) => setOptIn(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#B4552A]" data-testid="login-optin-checkbox" />
                  <span className="text-sm text-charcoal/85 leading-snug">Have you stayed with us? Get direct-booking deals — save 15% on your next stay.</span>
                </label>
                <button type="submit" disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 bg-copper text-cream font-extrabold rounded-full py-4 text-lg hover:bg-copperdeep transition-colors disabled:opacity-50"
                  data-testid="login-submit-button">
                  <KeyRound size={18} aria-hidden /> {busy ? "Sending…" : "Email my sign-in link"}
                </button>
              </form>
            </>
          )}
        </div>
        <Link to="/" className="flex items-center justify-center gap-1.5 text-sm text-soft hover:text-charcoal mt-5 transition-colors" data-testid="login-back-home">
          <ArrowLeft size={14} aria-hidden /> Back to Pebble Farms
        </Link>
      </div>
    </div>
  );
}
